import PropTypes from "prop-types";
import { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import ListItemButton from "@mui/material/ListItemButton";
// components
import MenuPopover from "../../components/MenuPopover";
import { markAllAsRead } from "src/DAL/Notification/Notification";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrIconTile from "src/design-system/components/GrIconTile";
import GrCountBadge from "src/design-system/components/GrCountBadge";
import { grPopoverPaperSx } from "src/design-system/components/GrMenu";

// Header popovers (messages, notifications): 380 wide on desktop; at phone width the
// popover keeps a 16px gutter each side. The list scrolls inside, header stays put.
const paperSx = {
  width: 380,
  ml: 0,
  maxWidth: `calc(100vw - ${gr.space[8]})`,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  ...grPopoverPaperSx,
  "& > .arrow": { display: "none" },
};
const listSx = {
  maxHeight: "min(440px, calc(100vh - 200px))",
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarWidth: "thin",
};

const headerSx = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: "none",
  px: gr.space[4],
  py: gr.space[3],
  borderBottom: `1px solid ${gr.color.line}`,
  fontFamily: gr.font.family,
};

const clampSx = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
});

// Icon and tile tone for each notification type (visual only).
function renderContent(notification) {
  const title = notification.title;

  if (notification.notification_type === "feed_like") {
    return { icon: icons.heart, tone: "brand", title };
  }
  if (notification.notification_type === "feed_comment_add") {
    return { icon: icons.chat, tone: "brand", title };
  }
  if (notification.notification_type === "feed_coins_share") {
    return { icon: icons.award, tone: "brand", title };
  }
  if (notification.notification_type === "member_left_group") {
    return { icon: icons.logOut, tone: "neutral", title };
  }
  if (notification.notification_type === "feed_added") {
    return { icon: icons.file, tone: "brand", title };
  }
  if (notification.notification_type === "feed_pending") {
    return { icon: icons.clock, tone: "neutral", title };
  }
  if (notification.notification_type === "feed_approved") {
    return { icon: icons.check, tone: "ok", title };
  }
  if (notification.notification_type === "feed_rejected") {
    return { icon: icons.close, tone: "overdue", title };
  }
  if (notification.notification_type === "member_removed_from_group") {
    return { icon: icons.personRemove, tone: "neutral", title };
  }
  if (notification.notification_type === "member_added_to_group") {
    return { icon: icons.personAdd, tone: "brand", title };
  }
  if (notification.notification_type === "friend_request") {
    return { icon: icons.people, tone: "brand", title };
  }
  if (notification.notification_type === "friend_request_acceptance") {
    return { icon: icons.people, tone: "ok", title };
  }
  if (notification.notification_type === "friend_request_rejection") {
    return { icon: icons.personRemove, tone: "neutral", title };
  }
  if (notification.notification_type === "accountability_partner_request") {
    return { icon: icons.people, tone: "brand", title };
  }
  if (notification.notification_type === "accountability_request_acceptance") {
    return { icon: icons.people, tone: "ok", title };
  }
  if (notification.notification_type === "deficit_off_notification") {
    return { icon: icons.energy, tone: "brand", title };
  }

  if (notification.notification_type === "WEIGHT_REMINDER") {
    return { icon: icons.activity, tone: "brand", title };
  }

  if (notification.notification_type === "general_admin") {
    return {
      icon: icons.info,
      tone: "brand",
      title,
      description: notification.description,
    };
  }

  return { icon: icons.bell, tone: "brand", title };
}

export default function NotificationsPopover({
  allNotifications,
  setUnreadNotificationCount,
  unreadNotificationCount,
  setAllNotifications,
  handleRedirect,
  setOpen,
  open,
}) {
  const anchorRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  NotificationItem.propTypes = {
    notification: PropTypes.object.isRequired,
  };
  function NotificationItem({ notification }) {
    const { icon, tone, title, description } = renderContent(notification);
    const unread = !notification.is_read;

    return (
      <ListItemButton
        to="#"
        disableGutters
        disableRipple
        component={RouterLink}
        sx={{
          alignItems: "flex-start",
          gap: gr.space[3],
          px: gr.space[4],
          py: gr.space[3],
          fontFamily: gr.font.family,
          background: unread ? gr.color.tint : gr.color.surface,
          transition: "background-color .15s ease",
          "&:not(:first-of-type)": { borderTop: `1px solid ${gr.color.line2}` },
          "&:hover": {
            background: unread ? gr.color.brandSoft : gr.color.tint,
          },
          "&.Mui-focusVisible": { background: gr.color.tint },
        }}
        onClick={(e) => {
          e.preventDefault();
          handleRedirect(
            notification._id,
            notification.notification_type,
            notification
          );
        }}
      >
        <GrIconTile
          size="sm"
          tone={unread ? tone : "neutral"}
          sx={unread ? undefined : { color: gr.color.muted }}
        >
          <GrIcon icon={icon} />
        </GrIconTile>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="p"
            title={title}
            sx={{
              m: 0,
              ...clampSx(2),
              fontSize: gr.font.size.sm,
              fontWeight: unread
                ? gr.font.weight.medium
                : gr.font.weight.regular,
              lineHeight: gr.font.lineHeight.normal,
              color: unread ? gr.color.ink : gr.color.ink2,
            }}
          >
            {title}
          </Box>
          {description && (
            <Box
              component="p"
              title={description}
              sx={{
                m: 0,
                mt: "2px",
                ...clampSx(2),
                fontSize: gr.font.size.sm,
                lineHeight: gr.font.lineHeight.normal,
                color: unread ? gr.color.ink2 : gr.color.muted,
                whiteSpace: "pre-line",
              }}
            >
              {description}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              mt: gr.space[1],
              display: "block",
              fontSize: gr.font.size.xs,
              lineHeight: gr.font.lineHeight.tight,
              color: gr.color.muted,
            }}
          >
            {formatDistanceToNow(new Date(notification.createdAt))} ago
          </Box>
        </Box>
        <Box
          component="span"
          role={unread ? "img" : undefined}
          aria-label={unread ? "Unread" : undefined}
          aria-hidden={unread ? undefined : true}
          sx={{
            flex: "none",
            mt: "6px",
            width: 8,
            height: 8,
            borderRadius: gr.radius.pill,
            background: unread ? gr.color.brand : "transparent",
          }}
        />
      </ListItemButton>
    );
  }
  const handleMarkAllAsRead = () => {
    setUnreadNotificationCount(0);
    markAllAsRead(); //api to mark all read
    setAllNotifications(
      allNotifications.map((notification) => ({
        ...notification,
        is_read: true,
      }))
    );
  };
  return (
    <>
      <GrIconButton
        ref={anchorRef}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadNotificationCount} color="error">
          <GrIcon icon={icons.bell} />
        </Badge>
      </GrIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={paperSx}
      >
        <Box sx={headerSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: gr.space[3] }}>
            <Box
              component="h2"
              sx={{
                m: 0,
                flexGrow: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: gr.space[2],
                fontSize: gr.font.size.lg,
                fontWeight: gr.font.weight.semibold,
                lineHeight: gr.font.lineHeight.tight,
                color: gr.color.ink,
              }}
            >
              Notifications
              <GrCountBadge
                count={unreadNotificationCount}
                tone="alert"
                label={`${unreadNotificationCount} unread notifications`}
              />
            </Box>
            {unreadNotificationCount > 0 && (
              <GrButton
                variant="text"
                size="sm"
                onClick={handleMarkAllAsRead}
                sx={{ flex: "none", mr: `calc(-1 * ${gr.space[2]})` }}
              >
                Mark all as read
              </GrButton>
            )}
          </Box>
          <Box
            component="p"
            sx={{
              m: 0,
              fontSize: gr.font.size.sm,
              lineHeight: gr.font.lineHeight.normal,
              color: gr.color.muted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            You have {unreadNotificationCount} unread notifications.
          </Box>
        </Box>

        <Box sx={listSx}>
          <Box>
            {allNotifications.map((notification, index) => (
              <NotificationItem key={index} notification={notification} />
            ))}
          </Box>
        </Box>
      </MenuPopover>
    </>
  );
}
